<template>
	<BaseLayout :footer-full="true" :include-padding="false" :transparent-header="true" :is-venture="true">
		<section class="venture-hero bg-color-content-primary text-color-white text-align-center">
			<img :src="heroImageUrl" alt="" class="venture-hero__image" fetchpriority="high" aria-hidden="true" />
			<div class="container venture-hero__content col-6-lg">
				<div class="venture-hero__header mb-400">
					<BaseSvgIcon name="fundrise-vcx-logo-stacked" width="111" height="50" title="VCX by Fundrise" />
					<span class="venture-hero__header-divider" aria-hidden="true" />
					<p class="venture-hero__header-tagline body-sm mt-0"
						>The public ticker<br />for private tech<sup>SM</sup></p
					>
				</div>
				<h1
					v-if="isMobile"
					class="heading-xl font-weight-lighter mb-150"
					v-html="deorphanText('VCX is now open for trading on the NYSE')"
				></h1>
				<h1 v-else class="heading-xl font-weight-lighter mb-150">
					VCX is now open for<br />trading on the NYSE</h1
				>
				<p class="venture-hero__content-description body-lg mt-0 mb-400">
					The Innovation Fund (ticker: VCX) is a public venture fund that gives you access to top private tech
					companies, from AI to space exploration, in a single&nbsp;investment.
				</p>
				<BaseLink :link="vcxCtaLink" classes="button button-vcx">Learn more about VCX</BaseLink>
			</div>
		</section>
		<section v-if="isPartnerPage" class="bg-color-content-primary">
			<BasePlaceHolder class="container pb-200" :placehold="loading" theme="dark" :line-count="3">
				<SolicitorNotice
					data-test="venture-solicitor-notice"
					:solicitor="solicitorNoticeDetails.name"
					:payment="solicitorNoticeDetails.payment"
					:is-cpa-partner="solicitorNoticeDetails.isCpaPartner"
					:is-investor="solicitorNoticeDetails.isInvestor"
					:monthly-payment="solicitorNoticeDetails.monthlyPayment"
					:custom-payment-condition="solicitorNoticeDetails.customCondition"
					:text-dark="false"
				/>
			</BasePlaceHolder>
		</section>
		<VentureCapitalWhyVenture />
		<VentureCapitalTheFundriseDifference v-if="cards" :section-header="differenceHeader" :cards="cards" />
		<RelatedArticlesSection
			v-if="investorUpdatesSection.relatedArticles.length"
			:heading="investorUpdatesSection.sectionHeading"
			:related-articles="investorUpdatesSection.relatedArticles"
		/>
		<template #footer>
			<BasePlaceHolder :line-count="3" :placehold="loading" :class="{ container: loading }">
				<FooterFull :disclaimers="footerDisclaimers" />
			</BasePlaceHolder>
		</template>
	</BaseLayout>
</template>
<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, Ref, ref } from 'vue';
import { LandingPageHeader, Link } from 'types/layout';
import { MarketingSiteCard, SolicitorNoticeDetails } from 'types/marketing-site';
import { appCloudfrontPath } from '@constants';
import { defaultSolicitorNotice } from '@constants/marketing-site';
import { deorphanText } from '@filters/shared-filters';
import FooterFull from '@components/footer/footer-full.vue';
import { getMediaPartnerNotices } from '@api/sanity/graphql/media-partner-notices';
import { getVentureMarketingPage } from '@api/sanity/graphql/venture-marketing-page';
import { mapInvestorUpdateArticleTeasers } from '@utils/blog';
import { MediaPartnerNoticeData } from 'types/sanity/marketing-components';
import RelatedArticlesSection from '@components/marketing-site/related-articles-section.vue';
import { SanityRelatedArticlesSection } from 'types/sanity/education-center';
import { SanityRichTextBlock } from 'types/sanity';
import SolicitorNotice from '@components/marketing-site/solicitor-notice.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';
import VentureCapitalTheFundriseDifference from '@components/marketing-site/venture-capital/venture-capital-the-fundrise-difference.vue';
import VentureCapitalWhyVenture from '@components/marketing-site/venture-capital/venture-capital-why-venture.vue';
import { VentureMarketingPage } from 'types/sanity/venture-marketing';

interface Props {
	isPartnerPage?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isPartnerPage: false
});

const route = useRoute();
const appStore = useAppStore();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const loading = ref(true);
const solicitorNoticeDetails: Ref<SolicitorNoticeDetails> = ref(defaultSolicitorNotice);

const sanityData: Ref<VentureMarketingPage | null> = ref(null);
const footerDisclaimers: Ref<Array<SanityRichTextBlock>> = ref([]);

const heroImageUrl = `${appCloudfrontPath}/images/vcx/vcx-hero.webp`;

const vcxCtaLink: Link = {
	router: 'vcx-landing',
	text: 'Learn more about VCX'
};

const investorUpdatesSection = computed((): SanityRelatedArticlesSection => {
	return {
		sectionHeading: sanityData.value?.investorUpdatesSection?.sectionHeading,
		relatedArticles: mapInvestorUpdateArticleTeasers(
			sanityData.value?.investorUpdatesSection?.relatedArticles || []
		)
	};
});

const utmSource = computed((): string => (route.query.utm_source ? route.query.utm_source.toString() : ''));

onBeforeMount(() => {
	if (props.isPartnerPage) {
		thirdPartyAuthStore.disable(onBeforeUnmount);
	}
});

const isMobile = computed((): boolean => {
	return appStore.isMobile;
});

const differenceHeader = computed((): LandingPageHeader | undefined => {
	const toutsPanel = sanityData.value?.toutsPanel;

	return {
		heading: toutsPanel?.panelHeading || '',
		summary: [toutsPanel?.panelSummary || ''],
		title: toutsPanel?.panelOverline || ''
	};
});

const cards = computed((): Array<MarketingSiteCard> | undefined => {
	const toutsPanel = sanityData.value?.toutsPanel?.panelTouts;
	const marketingCards: Array<MarketingSiteCard> = [];

	if (toutsPanel && toutsPanel?.length > 0) {
		toutsPanel.forEach((tout) => {
			marketingCards.push({
				title: tout.toutHeading || '',
				description: tout.toutSummary || '',
				link:
					tout.toutCta?.url && tout.toutCta?.linkText
						? {
								href: tout.toutCta?.url || undefined,
								target: tout.toutCta?.openInNewTab ? '_blank' : '_self',
								text: tout.toutCta?.linkText || undefined
							}
						: undefined
			});
		});
		return marketingCards;
	}
	return undefined;
});

async function setup(): Promise<void> {
	useHead({
		title: 'Venture Capital',
		meta: [
			{ property: 'og:title', content: 'Venture Capital' },
			{
				name: 'description',
				content: "Invest in tomorrow's great tech companies, today"
			},
			{
				property: 'og:description',
				content: "Invest in tomorrow's great tech companies, today"
			}
		]
	});
	try {
		const ventureMarketingSanityResponse = await getVentureMarketingPage('venture-capital');

		sanityData.value = ventureMarketingSanityResponse;
		footerDisclaimers.value = sanityData.value?.flatFooterDisclaimers ?? [];

		if (props.isPartnerPage) {
			const mediaPartners = await getMediaPartnerNotices('venture-capital');
			const partnerNotices = mediaPartners?.partnerNotices ?? [];
			const solicitorDetails: MediaPartnerNoticeData | null =
				partnerNotices.find((p: MediaPartnerNoticeData) => p.utmSource === utmSource.value) || null;

			const details: SolicitorNoticeDetails = {
				...defaultSolicitorNotice,
				name: solicitorDetails?.name || defaultSolicitorNotice.name,
				payment: solicitorDetails?.payment || defaultSolicitorNotice.payment,
				monthlyPayment: solicitorDetails?.monthlyPayment || defaultSolicitorNotice.monthlyPayment,
				isInvestor: solicitorDetails?.isInvestor || defaultSolicitorNotice.isInvestor,
				isCpaPartner: solicitorDetails?.isCpaPartner || defaultSolicitorNotice.isCpaPartner
			};

			solicitorNoticeDetails.value = details;
		}
	} catch {
	} finally {
		loading.value = false;
	}
}
setup();
</script>
<style lang="scss" scoped>
@use '../../styles/constants/colors' as *;
@use '../../styles/utilities/respond-to.scss' as *;
:deep(.button-vcx) {
	background-color: orange(40);
	border-color: orange(40);
	color: token('content-primary');

	&:hover {
		background-color: rgba(orange(40), 0.75);
		border-color: rgba(orange(40), 0.75);
	}
}

.venture-hero {
	position: relative;
	overflow: hidden;
	padding-top: 8rem;
	padding-bottom: 8rem;

	@include respond-to(smo) {
		padding-top: 6rem;
		padding-bottom: 6rem;
	}

	&__image {
		position: absolute;
		bottom: 0;
		left: 50%;
		transform: translateX(-50%);
		width: 100%;
		height: 55%;
		object-fit: cover;
		object-position: top center;
		pointer-events: none;
		z-index: 0;
	}

	&__content {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: 0 auto;

		&-description {
			max-width: 620px;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		gap: 0.875rem;

		&-divider {
			display: block;
			width: 1px;
			height: 3.125rem;
			background-color: rgba(255, 255, 255, 0.5);
		}

		&-tagline {
			font-family:
				'TT Firs Text',
				-apple-system,
				BlinkMacSystemFont,
				'Segoe UI',
				sans-serif;

			sup {
				font-size: 8px;
			}
		}
	}
}
</style>
